import React, { useState } from 'react'
import PropTypes from 'prop-types'
import {
  Card,
  Row,
  Col,
  Select,
  Switch,
  Input,
  Space,
  Typography,
  Divider,
  Button,
  Checkbox,
  Radio,
  theme,
  Tooltip,
  Alert
} from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faCog, faChevronDown, faChevronUp, faInfoCircle } from '@fortawesome/free-solid-svg-icons'

const { Text } = Typography
const { Option } = Select

const InputTypeConfigurator = ({ dataPoint, onConfigChange, initialConfig = {} }) => {
  const { token } = theme.useToken()
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [config, setConfig] = useState({
    inputType: 'dropdown',
    multiSelect: false,
    allowOther: false,
    required: false,
    defaultValue: null,
    placeholder: '',
    helpText: '',
    ...initialConfig
  })

  const handleConfigChange = (key, value) => {
    const newConfig = { ...config, [key]: value }

    // Auto-adjust related settings
    if (key === 'inputType') {
      if (value === 'radio') {
        newConfig.multiSelect = false // Radio is always single-select
      } else if (value === 'checkbox') {
        newConfig.multiSelect = true // Checkbox is always multi-select
      }
    }

    setConfig(newConfig)
    onConfigChange(newConfig)
  }

  const getInputTypeDescription = (type) => {
    const descriptions = {
      dropdown: 'Space-efficient, good for many options',
      radio: 'Mutually exclusive choices, few options',
      checkbox: 'Multiple selections allowed',
      text: 'Free text input for comments',
      numeric: 'Number input with validation'
    }
    return descriptions[type] || ''
  }

  const renderPreview = () => {
    const options = dataPoint.options || ['Option 1', 'Option 2', 'Option 3']

    switch (config.inputType) {
      case 'dropdown':
        return (
          <Select
            placeholder={config.placeholder || `Select ${dataPoint.label.toLowerCase()}`}
            style={{ width: '100%' }}
            mode={config.multiSelect ? 'multiple' : undefined}
            size='small'
            disabled
          >
            {options.map((option, index) => (
              <Option key={index} value={option}>
                {option}
              </Option>
            ))}
          </Select>
        )

      case 'radio':
        return (
          <Radio.Group disabled>
            <Space direction='vertical' size='small'>
              {options.slice(0, 3).map((option, index) => (
                <Radio key={index} value={option}>
                  {option}
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        )

      case 'checkbox':
        return (
          <Space direction='vertical' size='small'>
            {options.slice(0, 3).map((option, index) => (
              <Checkbox key={index} disabled>
                {option}
              </Checkbox>
            ))}
          </Space>
        )

      case 'text':
        return <Input placeholder={config.placeholder || 'Enter additional notes...'} disabled size='small' />

      case 'numeric':
        return (
          <Input
            placeholder={config.placeholder || `Enter ${dataPoint.label.toLowerCase()}...`}
            addonAfter={dataPoint.units}
            disabled
            size='small'
          />
        )

      default:
        return null
    }
  }

  return (
    <Card
      size='small'
      style={{
        marginTop: '8px',
        borderRadius: '6px',
        borderLeft: `3px solid ${token.colorPrimary}`,
        backgroundColor: '#fafbfc'
      }}
      title={
        <Space>
          <FontAwesomeIcon icon={faCog} style={{ color: token.colorPrimary, fontSize: '12px' }} />
          <Text strong style={{ fontSize: '13px' }}>
            Input Configuration
          </Text>
        </Space>
      }
      extra={
        <Button
          type='text'
          size='small'
          icon={<FontAwesomeIcon icon={showAdvanced ? faChevronUp : faChevronDown} />}
          onClick={() => setShowAdvanced(!showAdvanced)}
          style={{ fontSize: '11px' }}
        >
          {showAdvanced ? 'Basic' : 'Advanced'}
        </Button>
      }
    >
      <Row gutter={[12, 8]}>
        {/* Input Type Selection */}
        <Col span={12}>
          <div>
            <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
              Input Type
              <Tooltip title='How this field will be displayed to users'>
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  style={{ marginLeft: '4px', color: token.colorTextSecondary, fontSize: '10px' }}
                />
              </Tooltip>
            </Text>
            <Select
              value={config.inputType}
              onChange={(value) => handleConfigChange('inputType', value)}
              style={{ width: '100%' }}
              size='small'
              optionLabelProp='label'
            >
              <Option value='dropdown' label='Dropdown'>
                <div style={{ padding: '4px 0' }}>
                  <div style={{ fontSize: '13px', fontWeight: '500', marginBottom: '2px' }}>Dropdown</div>
                  <div style={{ fontSize: '11px', color: '#8c8c8c', lineHeight: '1.2' }}>
                    {getInputTypeDescription('dropdown')}
                  </div>
                </div>
              </Option>
              <Option value='radio' label='Radio Buttons'>
                <div style={{ padding: '4px 0' }}>
                  <div style={{ fontSize: '13px', fontWeight: '500', marginBottom: '2px' }}>Radio Buttons</div>
                  <div style={{ fontSize: '11px', color: '#8c8c8c', lineHeight: '1.2' }}>
                    {getInputTypeDescription('radio')}
                  </div>
                </div>
              </Option>
              <Option value='checkbox' label='Checkboxes'>
                <div style={{ padding: '4px 0' }}>
                  <div style={{ fontSize: '13px', fontWeight: '500', marginBottom: '2px' }}>Checkboxes</div>
                  <div style={{ fontSize: '11px', color: '#8c8c8c', lineHeight: '1.2' }}>
                    {getInputTypeDescription('checkbox')}
                  </div>
                </div>
              </Option>
              {showAdvanced && (
                <>
                  <Option value='text' label='Text Input'>
                    <div style={{ padding: '4px 0' }}>
                      <div style={{ fontSize: '13px', fontWeight: '500', marginBottom: '2px' }}>Text Input</div>
                      <div style={{ fontSize: '11px', color: '#8c8c8c', lineHeight: '1.2' }}>
                        {getInputTypeDescription('text')}
                      </div>
                    </div>
                  </Option>
                  <Option value='numeric' label='Numeric Input'>
                    <div style={{ padding: '4px 0' }}>
                      <div style={{ fontSize: '13px', fontWeight: '500', marginBottom: '2px' }}>Numeric Input</div>
                      <div style={{ fontSize: '11px', color: '#8c8c8c', lineHeight: '1.2' }}>
                        {getInputTypeDescription('numeric')}
                      </div>
                    </div>
                  </Option>
                </>
              )}
            </Select>
          </div>
        </Col>

        {/* Multi-Select Toggle */}
        {['dropdown'].includes(config.inputType) && (
          <Col span={12}>
            <div>
              <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                Allow Multiple
                <Tooltip title='Allow users to select multiple options'>
                  <FontAwesomeIcon
                    icon={faInfoCircle}
                    style={{ marginLeft: '4px', color: token.colorTextSecondary, fontSize: '10px' }}
                  />
                </Tooltip>
              </Text>
              <Switch
                checked={config.multiSelect}
                onChange={(checked) => handleConfigChange('multiSelect', checked)}
                size='small'
                checkedChildren='Multi'
                unCheckedChildren='Single'
              />
            </div>
          </Col>
        )}

        {/* Required Toggle */}
        <Col span={12}>
          <div>
            <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
              Required Field
              <Tooltip title='Make this field mandatory'>
                <FontAwesomeIcon
                  icon={faInfoCircle}
                  style={{ marginLeft: '4px', color: token.colorTextSecondary, fontSize: '10px' }}
                />
              </Tooltip>
            </Text>
            <Switch
              checked={config.required}
              onChange={(checked) => handleConfigChange('required', checked)}
              size='small'
              checkedChildren='Yes'
              unCheckedChildren='No'
            />
          </div>
        </Col>

        {/* Advanced Options */}
        {showAdvanced && (
          <>
            <Col span={24}>
              <Divider style={{ margin: '8px 0' }} />
            </Col>

            {/* Allow Other Option */}
            <Col span={12}>
              <div>
                <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                  Allow "Other"
                  <Tooltip title='Add an "Other" option with free text input'>
                    <FontAwesomeIcon
                      icon={faInfoCircle}
                      style={{ marginLeft: '4px', color: token.colorTextSecondary, fontSize: '10px' }}
                    />
                  </Tooltip>
                </Text>
                <Switch
                  checked={config.allowOther}
                  onChange={(checked) => handleConfigChange('allowOther', checked)}
                  size='small'
                  checkedChildren='Yes'
                  unCheckedChildren='No'
                />
              </div>
            </Col>

            {/* Placeholder Text */}
            <Col span={12}>
              <div>
                <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                  Placeholder Text
                </Text>
                <Input
                  value={config.placeholder}
                  onChange={(e) => handleConfigChange('placeholder', e.target.value)}
                  placeholder='Enter placeholder...'
                  size='small'
                />
              </div>
            </Col>

            {/* Help Text */}
            <Col span={24}>
              <div>
                <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '4px' }}>
                  Help Text
                </Text>
                <Input
                  value={config.helpText}
                  onChange={(e) => handleConfigChange('helpText', e.target.value)}
                  placeholder='Additional guidance for users...'
                  size='small'
                />
              </div>
            </Col>
          </>
        )}

        {/* Preview Section */}
        <Col span={24}>
          <Divider style={{ margin: '8px 0' }} />
          <div>
            <Text strong style={{ fontSize: '12px', display: 'block', marginBottom: '6px' }}>
              <FontAwesomeIcon icon={faEye} style={{ marginRight: '4px' }} />
              Preview
            </Text>
            <div
              style={{
                padding: '8px',
                background: 'white',
                border: '1px solid #e5e7eb',
                borderRadius: '4px'
              }}
            >
              <Text
                style={{ fontSize: '11px', color: token.colorTextSecondary, display: 'block', marginBottom: '4px' }}
              >
                {dataPoint.label}
                {config.required && <span style={{ color: token.colorError }}> *</span>}
              </Text>
              {renderPreview()}
              {config.helpText && (
                <Text type='secondary' style={{ fontSize: '10px', display: 'block', marginTop: '4px' }}>
                  {config.helpText}
                </Text>
              )}
            </div>
          </div>
        </Col>
      </Row>

      {/* Input Type Info */}
      {config.inputType && (
        <Alert
          message={getInputTypeDescription(config.inputType)}
          type='info'
          showIcon={false}
          style={{
            marginTop: '8px',
            fontSize: '11px',
            padding: '4px 8px',
            borderRadius: '4px'
          }}
        />
      )}
    </Card>
  )
}

InputTypeConfigurator.propTypes = {
  dataPoint: PropTypes.object.isRequired,
  onConfigChange: PropTypes.func.isRequired,
  initialConfig: PropTypes.object
}

export default InputTypeConfigurator
